import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Clock, Download, Eye, FileText, Lock, RefreshCw, Scale, Shield, Trash2, Upload } from 'lucide-react';
import { deleteObject, getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';

import { usePortalContext } from '../../context/PortalContext';
import { auth, storage } from '../../shared/api/firebase/config';
import { checkIsAdmin } from '../../services/AuthRepository';
import { FIREBASE_CONFIG } from '../../config/Config';
import { FS } from '../../shared/constants/firestorePaths';
import { logClientPortalEvent } from '../../utils/portalEventLog';
import {
  deleteDocument,
  fetchClientProcessIds,
  watchClientWorkspaces,
  watchClientGedLinks,
  subscribeClientDocuments,
  subscribeLegacyDocuments,
  subscribeDocumentAcknowledgements,
  subscribeLegacyAcknowledgements,
  writeDocumentAcknowledgement,
  writeLegacyAcknowledgement,
  addClientViewNote,
  updateDocumentSignature,
} from '../../services/PortalVaultRepository.js';
import PortalPageHeader from '../../components/portal/PortalPageHeader';
import { logClientPortalEventOnce } from '../../utils/portalEventLog';
import { resolveDocumentUrl } from '../../utils/documentResolver';
import CadernoAccordion from '../../components/ui/CadernoAccordion';
import { checkPortalIntegrity } from '../../utils/workspaceIntegrityGuard';
import { checkDocumentsIntegrity } from '../../utils/bidirectionalIntegrityGuard';

const appId = FIREBASE_CONFIG.projectId;

// Backend Drive proxy (mesma fonte que ClientDriveView/master usam para nomes reais).
const getBackendBaseUrl = () =>
 {
  const envUrl = String(import.meta?.env?.VITE_BACKEND_URL || '').trim();
  if (envUrl) return envUrl.replace(/\/+$/, '');
  return 'https://rda-backend-thuq.onrender.com';
};

const toDateLabel = (v) => {
  try {
    if (v?.toDate) return v.toDate().toLocaleDateString();
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString();
  } catch {
    return '—';
  }
};

const resolveTitle = (row) => {
  return String(row?.title || row?.name || row?.fileName || row?.filename || '').trim() || 'Documento';
};


const resolveConsentBadge = (row) => {
  const cs = String(row?.consentStatus || '').trim().toLowerCase();
  if (cs === 'accepted' || cs === 'consent_accepted' || cs === 'signed') return { label: 'Consentimento Dado', className: 'bg-emerald-50 text-emerald-700 border-emerald-200', iconType: 'accepted' };
  if (cs === 'declined' || cs === 'consent_declined') return { label: 'Consentimento Recusado', className: 'bg-red-50 text-red-700 border-red-200', iconType: 'declined' };
  if (cs === 'review_pending') return { label: 'Em Análise', className: 'bg-yellow-50 text-yellow-700 border-yellow-200', iconType: 'review_pending' };
  if (requiresPendingConsent(row)) return { label: 'Consentimento Pendente', className: 'bg-amber-50 text-amber-700 border-amber-200', iconType: 'pending' };
  return null;
};

const resolveDocTypeStyle = (category) => {
  const cat = String(category || '').trim().toLowerCase();
  if (cat.includes('procura')) return { badgeClass: 'border-[#20243B]/20 bg-[#20243B]/5 text-[#20243B]', iconType: 'shield' };
  if (cat.includes('contrat')) return { badgeClass: 'border-purple-200 bg-purple-50 text-purple-700', iconType: 'file' };
  return { badgeClass: 'border-[#C5A059]/30 bg-[#C5A059]/10 text-[#C5A059]', iconType: 'default' };
};

const resolveFileUrl = (row) => {
  return String(row?.fileUrl || row?.url || row?.webViewLink || row?.downloadUrl || '').trim();
};

const requiresPendingConsent = (row) => {
  const requiresConsent = Boolean(row?.requiresConsent);
  const consentStatus = String(row?.consentStatus || '').trim().toLowerCase();
  const status = String(row?.status || '').trim().toLowerCase();
  return requiresConsent && (consentStatus === 'pending_consent' || status === 'pending_consent');
};

const isPublishedHtmlDocument = (row) => {
  const mimeType = String(row?.mimeType || '').trim().toLowerCase();
  const extension = String(row?.extension || '').trim().toLowerCase();
  const storagePath = String(row?.storagePath || row?.storage_path || '').trim().toLowerCase();
  const type = String(row?.type || '').trim().toUpperCase();
  const status = String(row?.status || '').trim().toUpperCase();
  const url = resolveFileUrl(row).toLowerCase();

  if (mimeType === 'text/html') return true;
  if (extension === 'html' || extension === '.html') return true;
  if (storagePath.endsWith('.html')) return true;
  if (url.includes('.html')) return true;
  return type === 'PROCURATION' && status === 'PUBLISHED';
};

export default function PortalVault() {
  const { crmClient, previewMode } = usePortalContext();
  const [docs, setDocs] = useState([]);
  const [workspaceRows, setWorkspaceRows] = useState([]);
  const [gedLinks, setGedLinks] = useState([]);
  // Map driveFileId -> real fileName fetched from Drive backend (mesma fonte que master/Drive)
  const [driveNamesByFileId, setDriveNamesByFileId] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [integrityIssues, setIntegrityIssues] = useState([]);
  const [viewedByDocId, setViewedByDocId] = useState({});
  const [processIds, setProcessIds] = useState([]);
  const [uploadingDocId, setUploadingDocId] = useState(null);
  const [uploadFeedback, setUploadFeedback] = useState({});
  const uploadInputRefs = useRef({});

  // Em Preview Admin, força isAdmin=false para simular fielmente o que o cliente vê.
  const isAdmin = useMemo(() => !previewMode && checkIsAdmin(user), [previewMode]);

  const handleRevokeDocument = useCallback(async (row) => {
    const docId = String(row?.id || '').trim();
    const title = resolveTitle(row);
    if (!docId || !appId) return;
    if (!window.confirm(`Tem certeza que deseja revogar e excluir o documento "${title}"? Esta ação é irreversível.`)) return;
    try {
      // [UU-01 LGPD] ORDEM CRÍTICA: Storage primeiro, Firestore depois.
      // Se Storage falhar, Firestore ainda aponta o arquivo (possível retry).
      // Se Firestore falhar após Storage OK, ficamos com metadado órfão — aceitável
      // (cliente não vê o arquivo sem metadado, LGPD cumprida pois o PDF foi deletado).
      const storagePathCandidates = [
        row?.storagePath,
        row?.storageEngine?.storagePath,
        row?.payload?.storagePath,
      ];
      const storagePath = storagePathCandidates
        .map((v) => String(v || '').trim())
        .find((v) => v.length > 0) || '';

      if (storagePath) {
        try {
          await deleteObject(storageRef(storage, storagePath));
        } catch (storageErr) {
          // objeto já inexistente não deve impedir a revogação do metadado
          if (storageErr?.code !== 'storage/object-not-found') {
            console.warn('[RDA] Falha ao apagar arquivo do Storage (LGPD):', storageErr);
          }
        }
      }

      await deleteDocument(docId);
    } catch (e) {
      console.error('[RDA] Erro ao revogar documento:', e);
      window.alert('Erro ao revogar o documento: ' + (e?.message || String(e)));
    }
  }, []);

  const clientId = useMemo(() => {
    return crmClient?.id ? String(crmClient.id) : '';
  }, [crmClient?.id]);

  const emailCandidates = useMemo(() => {
    const out = [];
    const seen = new Set();
    [crmClient?.contact?.email, crmClient?.email].forEach((raw) => {
      const safe = String(raw || '').trim();
      const key = safe.toLowerCase();
      if (!safe || seen.has(key)) return;
      seen.add(key);
      out.push(safe);
    });
    return out;
  }, [crmClient?.contact?.email, crmClient?.email]);

  const clientName = useMemo(() => {
    return (
      String(crmClient?.fullName || '').trim() ||
      String(crmClient?.qualification?.physical?.socialName || '').trim() ||
      String(crmClient?.qualification?.physical?.name || '').trim() ||
      String(crmClient?.contact?.name || '').trim() ||
      'Cliente'
    );
  }, [crmClient?.fullName, crmClient?.qualification?.physical?.socialName, crmClient?.qualification?.physical?.name, crmClient?.contact?.name]);

  const workspaceById = useMemo(() => {
    const out = new Map();
    (Array.isArray(workspaceRows) ? workspaceRows : []).forEach((w) => {
      const wid = String(w?.id || '').trim();
      if (!wid) return;
      out.set(wid, w);
    });
    return out;
  }, [workspaceRows]);

  const gedDocs = useMemo(() => {
    const safe = Array.isArray(gedLinks) ? gedLinks : [];
    return safe
      .filter((row) => {
        const wsId = String(row?.workspaceId || '').trim();
        return wsId && workspaceById.has(wsId);
      })
      .map((row) => {
        const workspaceId = String(row?.workspaceId || '').trim();
        const workspace = workspaceById.get(workspaceId) || {};
        const driveFileId = String(row?.driveFileId || '').trim();
        const driveUrl = String(
          row?.fileUrl ||
          row?.webViewLink ||
          row?.externalUrl ||
          (driveFileId ? `https://drive.google.com/file/d/${driveFileId}/view` : '')
        ).trim();

        // Nome real: prioridade absoluta para o nome do Google Drive (Source of Truth).
        // Mesma fonte que master e ClientDriveView usavam quando "funcionava bonitinho".
        const rawFileName = String(row?.fileName || '').trim();
        const driveName = driveNamesByFileId[driveFileId] ? String(driveNamesByFileId[driveFileId]).trim() : '';
        const realTitle = driveName || rawFileName || '';

        return {
          id: `ged_${String(row?.id || `${workspaceId}_${driveFileId}`)}`,
          title: realTitle,
          name: realTitle,
          fileName: realTitle,
          fileUrl: driveUrl,
          webViewLink: driveUrl,
          externalUrl: driveUrl,
          cadernoId: workspaceId || '__AVULSO__',
          cadernoTitle: String(workspace?.alias || workspace?.name || '').trim(),
          processNumber: String(workspace?.ancestry?.primary_process_id || workspace?.processNumber || '').trim(),
          juizo: String(workspace?.juizo || '').trim(),
          comarca: String(workspace?.comarca || '').trim(),
          classification: { category: 'Anexo do Caderno' },
          category: 'Anexo do Caderno',
          clientVisible: true,
          requiresConsent: false,
          _source: 'ged_link',
          createdAt: row?.addedAt || null,
          updatedAt: row?.addedAt || null,
        };
      });
  }, [gedLinks, workspaceById, driveNamesByFileId]);

  const allDocs = useMemo(() => {
    // When workspaces exist, ClientDriveView is the authoritative source for Drive attachments
    // — exclude gedDocs to avoid phantom counts in the stats bar.
    const includeGedDocs = workspaceRows.length === 0;
    return [
      ...(Array.isArray(docs) ? docs : []),
      ...(includeGedDocs ? (Array.isArray(gedDocs) ? gedDocs : []) : []),
    ];
  }, [docs, gedDocs, workspaceRows]);

  const consolidatedDocs = useMemo(() => {
    const safe = Array.isArray(allDocs) ? allDocs : [];
    const visibleDocs = safe.filter((d) => d?.clientVisible !== false);
    if (visibleDocs.length === 0) return [];
    const seriesMap = new Map();
    const standalone = [];
    for (const row of visibleDocs) {
      const seriesId = String(row?.publicationSeriesId || '').trim();
      if (!seriesId) { standalone.push(row); continue; }
      if (!seriesMap.has(seriesId)) seriesMap.set(seriesId, []);
      seriesMap.get(seriesId).push(row);
    }
    const merged = [];
    for (const [, group] of seriesMap) {
      if (group.length === 1) { merged.push(group[0]); continue; }
      group.sort((a, b) => {
        const vDiff = (Number(b?.publicationVersion || 0) || 0) - (Number(a?.publicationVersion || 0) || 0);
        if (vDiff !== 0) return vDiff;
        const at = a?.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a?.createdAt || 0).getTime();
        const bt = b?.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b?.createdAt || 0).getTime();
        return (bt || 0) - (at || 0);
      });
      const consentPending = group.find((d) => requiresPendingConsent(d));
      const consentAccepted = group.find((d) => {
        const cs = String(d?.consentStatus || '').trim().toLowerCase();
        return cs === 'accepted' || cs === 'consent_accepted';
      });
      const pdfDoc = group.find((d) => String(d?.extension || '').toLowerCase() === 'pdf' || String(d?.mimeType || '').toLowerCase() === 'application/pdf');
      const bestBase = pdfDoc || group[0];
      const representative = { ...bestBase };
      if (consentPending) {
        representative.requiresConsent = true;
        representative.consentStatus = consentPending.consentStatus || 'pending_consent';
        representative.status = consentPending.status || 'pending_consent';
        representative._consentDocId = consentPending.id;
      } else if (consentAccepted) {
        representative.requiresConsent = true;
        representative.consentStatus = 'accepted';
      }
      representative._mergedCount = group.length;
      merged.push(representative);
    }
    return [...merged, ...standalone].sort((a, b) => {
      const av = a?.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a?.createdAt || 0).getTime();
      const bv = b?.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b?.createdAt || 0).getTime();
      return (bv || 0) - (av || 0);
    });
  }, [allDocs]);

  const pendingConsentCount = useMemo(() => {
    return consolidatedDocs.filter((row) => requiresPendingConsent(row)).length;
  }, [consolidatedDocs]);

  const groupedByCaderno = useMemo(() => {
    // Heurística matter-centric (padrão NetDocuments 2026):
    // Se cliente tem exatamente UM caderno e há docs órfãos (sem cadernoId),
    // auto-vincula esses órfãos ao caderno único. Resolve cenários onde docs
    // antigos foram criados sem cadernoId mas pertencem logicamente ao processo.
    const safeWs = Array.isArray(workspaceRows) ? workspaceRows : [];
    const singleWorkspace = safeWs.length === 1 ? safeWs[0] : null;
    const autoLinkCadernoId = singleWorkspace ? String(singleWorkspace?.id || '').trim() : '';
    const autoLinkCadernoTitle = singleWorkspace ? String(singleWorkspace?.alias || singleWorkspace?.name || '').trim() : '';
    const autoLinkProcessNumber = singleWorkspace ? String(singleWorkspace?.ancestry?.primary_process_id || singleWorkspace?.processNumber || '').trim() : '';
    const autoLinkJuizo = singleWorkspace ? String(singleWorkspace?.juizo || '').trim() : '';
    const autoLinkComarca = singleWorkspace ? String(singleWorkspace?.comarca || '').trim() : '';

    const groups = new Map();
    consolidatedDocs.forEach((row) => {
      let cid = String(row?.cadernoId || '').trim();
      let cTitle = String(row?.cadernoTitle || '').trim();
      // Auto-link: se não tem caderno e há um único caderno disponível, anexa ali
      if (!cid && autoLinkCadernoId) {
        cid = autoLinkCadernoId;
        if (!cTitle) cTitle = autoLinkCadernoTitle;
      }
      const groupKey = cid || '__AVULSO__';
      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          cadernoId: groupKey,
          cadernoTitle: cTitle || (groupKey === autoLinkCadernoId ? autoLinkCadernoTitle : ''),
          processNumber: groupKey === autoLinkCadernoId ? autoLinkProcessNumber : '',
          juizo: groupKey === autoLinkCadernoId ? autoLinkJuizo : '',
          comarca: groupKey === autoLinkCadernoId ? autoLinkComarca : '',
          docs: [],
        });
      }
      const g = groups.get(groupKey);
      if (cTitle && !g.cadernoTitle) g.cadernoTitle = cTitle;
      if (!g.processNumber && row?.processNumber) g.processNumber = String(row.processNumber).trim();
      if (!g.juizo && row?.juizo) g.juizo = String(row.juizo).trim();
      if (!g.comarca && row?.comarca) g.comarca = String(row.comarca).trim();
      g.docs.push(row);
    });

    // Garante que o caderno único apareça mesmo se não houver docs (cabeçalho informativo)
    if (autoLinkCadernoId && !groups.has(autoLinkCadernoId)) {
      groups.set(autoLinkCadernoId, {
        cadernoId: autoLinkCadernoId,
        cadernoTitle: autoLinkCadernoTitle,
        processNumber: autoLinkProcessNumber,
        juizo: autoLinkJuizo,
        comarca: autoLinkComarca,
        docs: [],
      });
    }

    // Sort INTERNO de cada caderno: procurações/contratos no topo,
    // depois anexos em ordem natural alfanumérica (ANEXO 01 < 02 < ... < 12).
    const naturalCompare = (a, b) => {
      const titleA = String(a?.title || a?.fileName || a?.name || '').trim();
      const titleB = String(b?.title || b?.fileName || b?.name || '').trim();
      return titleA.localeCompare(titleB, 'pt-BR', { numeric: true, sensitivity: 'base' });
    };
    const isProcuracaoOrContrato = (r) => {
      const t = String(r?.type || '').trim().toUpperCase();
      const c = String(r?.classification?.category || r?.category || '').toLowerCase();
      return t === 'PROCURATION' || t === 'CONTRACT' || c.includes('procura') || c.includes('contrat');
    };
    groups.forEach((g) => {
      g.docs.sort((a, b) => {
        const pa = isProcuracaoOrContrato(a) ? 0 : 1;
        const pb = isProcuracaoOrContrato(b) ? 0 : 1;
        if (pa !== pb) return pa - pb;
        return naturalCompare(a, b);
      });
    });

    const arr = Array.from(groups.values()).filter((g) => g.docs.length > 0);
    arr.sort((a, b) => {
      if (a.cadernoId === '__AVULSO__') return 1;
      if (b.cadernoId === '__AVULSO__') return -1;
      return (a.cadernoTitle || '').localeCompare(b.cadernoTitle || '', 'pt-BR');
    });
    return arr;
  }, [consolidatedDocs, workspaceRows]);

  useEffect(() => {
    if (!clientId || loading) return;
    const wsResult = checkPortalIntegrity({
      workspaces: workspaceRows,
      docs,
      gedLinks,
      clientId,
    });
    const docResult = checkDocumentsIntegrity({ docs, clientId });
    setIntegrityIssues([...(wsResult.issues || []), ...(docResult.issues || [])]);
  }, [clientId, workspaceRows, docs, gedLinks, loading]);

  useEffect(() => {
    if (!clientId) return;

    (async () => {
      try {
        await logClientPortalEventOnce({
          clientId,
          type: 'CLIENT_PORTAL_VAULT_VIEWED',
          actorName: clientName,
          payload: {
            text: 'Cliente acessou o acervo documental no portal.',
          },
          storage: 'session',
        });
      } catch (e) {
        void e;
      }
    })();
  }, [clientId, clientName]);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      if (!clientId && emailCandidates.length === 0) {
        if (!alive) return;
        setProcessIds([]);
        return;
      }

      try {
        const merged = await fetchClientProcessIds(clientId);
        if (!alive) return;
        setProcessIds(merged);
      } catch {
        if (!alive) return;
        setProcessIds([]);
      }
    };

    run();

    return () => {
      alive = false;
    };
  }, [clientId]);

  useEffect(() => {
    let unsub = null;
    let mounted = true;

    if (!clientId) {
      setWorkspaceRows([]);
      return () => { mounted = false; };
    }

    const hasAppId = String(appId || '').trim();
    if (!hasAppId) {
      setWorkspaceRows([]);
      return () => { mounted = false; };
    }

    try {
      unsub = watchClientWorkspaces(
        clientId,
        (snap) => {
          if (!mounted) return;
          const out = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
          setWorkspaceRows(out);
        },
        () => {
          if (!mounted) return;
          setWorkspaceRows([]);
        }
      );
    } catch {
      setWorkspaceRows([]);
    }

    return () => {
      mounted = false;
      try { unsub && unsub(); } catch {}
    };
  }, [clientId]);

  useEffect(() => {
    let unsub = null;
    let mounted = true;

    if (!clientId) {
      setGedLinks([]);
      return () => {
        mounted = false;
      };
    }

    try {
      unsub = watchClientGedLinks(
        clientId,
        (snap) => {
          if (!mounted) return;
          const out = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
          setGedLinks(out);
        },
        () => {
          if (!mounted) return;
          setGedLinks([]);
        }
      );
    } catch {
      setGedLinks([]);
    }

    return () => {
      mounted = false;
      try {
        unsub && unsub();
      } catch {
        // no-op
      }
    };
  }, [clientId]);

  // [DRIVE-NAMES] Busca nomes reais do Google Drive para enriquecer rda_ged_links
  // (cujo fileName pode estar vazio no Firestore). Mesma fonte que ClientDriveView/master.
  useEffect(() => {
    let cancelled = false;
    const safeWs = Array.isArray(workspaceRows) ? workspaceRows : [];
    if (safeWs.length === 0) return undefined;

    // Coleta todos os folder IDs únicos onde podem estar os anexos
    const folderIds = new Set();
    safeWs.forEach((w) => {
      ['attachmentsFolderId', 'driveFolderId', 'processPieceFolderId'].forEach((field) => {
        const v = String(w?.[field] || '').trim();
        if (v) folderIds.add(v);
      });
    });
    if (folderIds.size === 0) return undefined;

    (async () => {
      try {
        const token = await auth.currentUser?.getIdToken().catch(() => null);
        if (!token || cancelled) return;
        const baseUrl = getBackendBaseUrl();
        if (!baseUrl) return;

        const nameMap = {};
        await Promise.all(Array.from(folderIds).map(async (fid) => {
          try {
            const resp = await fetch(`${baseUrl}/drive/folder/${encodeURIComponent(fid)}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (!resp.ok) return;
            const data = await resp.json();
            const files = Array.isArray(data) ? data : [];
            files.forEach((f) => {
              const id = String(f?.id || '').trim();
              const name = String(f?.name || '').trim();
              if (id && name) nameMap[id] = name;
            });
          } catch { void 0; }
        }));

        if (!cancelled && Object.keys(nameMap).length > 0) {
          setDriveNamesByFileId((prev) => ({ ...prev, ...nameMap }));
        }
      } catch (e) {
        void e;
      }
    })();

    return () => { cancelled = true; };
  }, [workspaceRows]);

  useEffect(() => {
    let mounted = true;
    let unsubs = [];
    let switched = false;
    let firstSnap = true;

    const cleanup = () => {
      unsubs.forEach((fn) => {
        try {
          fn && fn();
        } catch (e) {
          void e;
        }
      });
      unsubs = [];
    };

    const setFailure = (msg) => {
      if (!mounted) return;
      setError(msg);
      setDocs([]);
      setLoading(false);
    };



    if (!clientId) {
      setDocs([]);
      setError('');
      setLoading(false);
      return () => {
        mounted = false;
        cleanup();
      };
    }

    setLoading(true);
    setError('');

    const startRoot = (baseError) => {
      if (!mounted) return;
      if (switched) return;
      switched = true;
      cleanup();
      firstSnap = true;

      unsubs = [subscribeLegacyDocuments(
        clientId,
        (docs) => { setDocs(docs); setLoading(false); },
        (e2) => setFailure(String(e2?.message || e2 || baseError || 'Falha ao carregar documentos'))
      )];
    };

    unsubs = [subscribeClientDocuments(
      clientId,
      (docs) => { setDocs(docs); if (firstSnap) { firstSnap = false; setLoading(false); } },
      (e) => startRoot(String(e?.message || e || 'Falha ao carregar documentos'))
    )];

    return () => {
      mounted = false;
      cleanup();
    };
  }, [clientId]);

  useEffect(() => {
    let unsub = null;
    let mounted = true;

    const setFailure = () => {
      if (!mounted) return;
      setViewedByDocId({});
    };



    if (!clientId) {
      setFailure();
      return () => {
        mounted = false;
      };
    }

    const hasAppId = String(appId || '').trim();

    unsub = subscribeDocumentAcknowledgements(
      clientId,
      (out) => setViewedByDocId(out),
      (_err) => {
        try {
          unsub && unsub();
        } catch (err) { void err; }
        unsub = subscribeLegacyAcknowledgements(
          clientId,
          (out) => setViewedByDocId(out),
          () => setFailure()
        );
      }
    );

    return () => {
      mounted = false;
      try {
        unsub && unsub();
      } catch (e) {
        void e;
      }
    };
  }, [clientId]);

  const handleViewDocument = useCallback(
    async (row) => {
      const fallbackUrl = resolveFileUrl(row);
      const url = String(await resolveDocumentUrl(row).catch(() => fallbackUrl) || fallbackUrl).trim();
      const documentId = String(row?.id || '').trim();
      const title = resolveTitle(row);

      const registerView = async ({ emitClientNote = true } = {}) => {
        if (!clientId) return;
        if (!documentId) return;

        setViewedByDocId((prev) => ({ ...(prev || {}), [documentId]: true }));

        try {
          const rawAckId = `${clientId}__${documentId}`;
          const ackId = rawAckId.replace(/[/\\#?]/g, '_').slice(0, 140);

          let firstError = null;

          try {
            await writeDocumentAcknowledgement(ackId, {
              documentId,
              clientId,
            });
          } catch (e) {
            firstError = e;
          }

          if (emitClientNote) {
            try {
              await addClientViewNote(clientId, {
                category: 'USER',
                type: 'CLIENT_DOCUMENT_VIEWED',
                actor: {
                  name: 'Cliente',
                  isBot: false,
                },
                payload: {
                  documentId,
                  title,
                  text: `Documento visualizado pelo cliente: ${title}`,
                  fileUrl: url,
                },
              });
            } catch (e) {
              if (!firstError) firstError = e;
            }
          }

          try {
            await writeLegacyAcknowledgement(ackId, {
              documentId,
              clientId,
            });
          } catch (e) {
            if (firstError) {
              console.error('[PORTAL][ACK] falha artifacts + raiz', firstError, e);
            } else {
              console.error('[PORTAL][ACK] falha raiz', e);
            }
          }
        } catch (e) {
          console.error('[PORTAL][ACK] falha ao registrar leitura', e);
        }
      };

      if (isPublishedHtmlDocument(row) && (url || documentId)) {
        const viewerUrl = `/view-published?${new URLSearchParams({
          ...(url ? { url } : {}),
          ...(documentId ? { documentId } : {}),
          ...(title ? { title } : {}),
        }).toString()}`;

        try {
          window.open(viewerUrl, '_blank', 'noopener,noreferrer');
          void registerView({ emitClientNote: false });
          return;
        } catch (e) {
          void e;
        }

        try {
          window.open(viewerUrl, '_blank');
          void registerView({ emitClientNote: false });
          return;
        } catch (e2) {
          void e2;
        }
      } else {
        if (!url) return;

        try {
          window.open(url, '_blank', 'noopener,noreferrer');
          void registerView({ emitClientNote: false });
          return;
        } catch (e2) {
          void e2;
        }

        try {
          window.open(url, '_blank');
          void registerView({ emitClientNote: false });
          return;
        } catch (e3) {
          void e3;
        }
      }

      await registerView({ emitClientNote: false });
    },
    [clientId]
  );

  const handleDownloadOriginal = useCallback(async (row) => {
    if (isPublishedHtmlDocument(row)) {
      const documentId = String(row?.id || '').trim();
      const title = resolveTitle(row);
      const fallbackUrl = resolveFileUrl(row);
      const url = String(await resolveDocumentUrl(row).catch(() => fallbackUrl) || fallbackUrl).trim();
      const viewerUrl = `/view-published?${new URLSearchParams({
        ...(url ? { url } : {}),
        ...(documentId ? { documentId } : {}),
        ...(title ? { title } : {}),
        print: '1',
      }).toString()}`;
      try {
        window.open(viewerUrl, '_blank', 'noopener,noreferrer');
      } catch {
        window.open(viewerUrl, '_blank');
      }
      return;
    }

    const fallbackUrl = resolveFileUrl(row);
    const url = String(await resolveDocumentUrl(row).catch(() => fallbackUrl) || fallbackUrl).trim();
    if (!url) return;
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = resolveTitle(row) + '.pdf';
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => document.body.removeChild(a), 500);
    } catch {
      window.open(url, '_blank');
    }
  }, []);

  const handleUploadSignedDocument = useCallback(async (file, row) => {
    if (!file || !row?.id || !clientId) return;
    const docId = String(row.id).trim();
    const safeAppId = String(appId || '').trim();
    if (!safeAppId) return;

    // [Q2 — Append-Only Guard] Avisa se já existe upload pendente de análise
    if (row.signedPdfUrl && String(row.consentStatus || row.status || '').toLowerCase().includes('review')) {
      const confirmReplace = window.confirm(
        'Já existe um documento assinado em análise pelo escritório.\n\nDeseja substituí-lo? (O envio anterior ficará registrado na trilha de auditoria.)'
      );
      if (!confirmReplace) return;
    }

    setUploadingDocId(docId);
    setUploadFeedback((prev) => ({ ...prev, [docId]: { type: 'loading', text: 'Enviando documento assinado...' } }));

    try {
      const ext = String(file.name || '').includes('.') ? file.name.slice(file.name.lastIndexOf('.')) : '.pdf';
      const safeName = `signed_${docId}_${Date.now()}${ext}`;
      const path = `artifacts/${safeAppId}/clients/${clientId}/signed_documents/${safeName}`;
      const fileRef = storageRef(storage, path);

      await uploadBytes(fileRef, file, { contentType: file.type || 'application/pdf' });
      const downloadUrl = await getDownloadURL(fileRef);

      await updateDocumentSignature(docId, {
        signedPdfUrl: downloadUrl,
        signedPdfStoragePath: path,
        signedPdfFileName: file.name || safeName,
        signatureMethod: 'external',
        consentStatus: 'review_pending',
        status: 'REVIEW_PENDING',
        signedUploadedBy: clientName || 'Cliente',
      });

      const category = String(row?.classification?.category || row?.category || '').trim();
      const catLower = category.toLowerCase();
      const isSignatureDoc = catLower.includes('procura') || catLower.includes('contrat');
      const title = resolveTitle(row);

      await logClientPortalEvent({
        clientId,
        type: isSignatureDoc ? 'CLIENT_SIGNED_DOCUMENT_UPLOADED' : 'CLIENT_CONSENT_DOCUMENT_UPLOADED',
        actorName: clientName || 'Cliente',
        category: 'SYSTEM',
        payload: {
          documentId: docId,
          fileName: title,
          signedPdfUrl: downloadUrl,
          signatureMethod: 'external',
          consentStatus: 'review_pending',
          workspaceLabel: category || 'Documento',
          text: isSignatureDoc
            ? `Documento assinado enviado pelo cliente: ${title}`
            : `Documento com consentimento enviado pelo cliente: ${title}`,
          consentRecord: {
            decision: 'uploaded_for_review',
            actionType: isSignatureDoc ? 'signature' : 'informed_consent',
            actorName: clientName || 'Cliente',
            decidedAtIso: new Date().toISOString(),
            documentId: docId,
            documentTitle: title,
            documentCategory: category || 'Documento',
            userAgent: typeof navigator !== 'undefined' ? String(navigator.userAgent || '') : '',
            ipNote: 'Upload de documento assinado via Portal do Cliente RDA',
          },
        },
      }).catch(() => {});

      setUploadFeedback((prev) => ({ ...prev, [docId]: { type: 'success', text: 'Documento assinado enviado com sucesso! O escritório analisará em breve.' } }));
      setTimeout(() => setUploadFeedback((prev) => { const next = { ...prev }; delete next[docId]; return next; }), 6000);
    } catch (err) {
      setUploadFeedback((prev) => ({ ...prev, [docId]: { type: 'error', text: String(err?.message || 'Falha ao enviar. Tente novamente.') } }));
      setTimeout(() => setUploadFeedback((prev) => { const next = { ...prev }; delete next[docId]; return next; }), 8000);
    } finally {
      setUploadingDocId(null);
    }
  }, [clientId, clientName]);

  return (
    <div className="space-y-4">
      <PortalPageHeader
        icon={<Lock className="w-5 h-5 text-[#C5A059]" />}
        title="Meu Cofre"
        subtitle="Contratos, petições e documentos oficiais compartilhados pelo escritório."
        badge="Cofre"
      />

      <div className="border border-gray-200 bg-white rounded-lg px-3 py-2 flex items-center gap-4">
        <div className="flex items-baseline gap-1">
          <span className="text-sm font-bold text-[#20243B]">{consolidatedDocs.length}</span>
          <span className="text-[10px] text-gray-500">doc{consolidatedDocs.length === 1 ? '' : 's'}</span>
        </div>
        <div className="w-px h-3 bg-gray-200" />
        <div className="flex items-baseline gap-1">
          <span className="text-sm font-bold text-emerald-600">{consolidatedDocs.filter((d) => viewedByDocId[String(d?.id || '')]).length}</span>
          <span className="text-[10px] text-gray-500">visualizado{consolidatedDocs.filter((d) => viewedByDocId[String(d?.id || '')]).length === 1 ? '' : 's'}</span>
        </div>
        <div className="w-px h-3 bg-gray-200" />
        <div className="flex items-baseline gap-1">
          <span className="text-sm font-bold text-[#C5A059]">{consolidatedDocs.filter((d) => !viewedByDocId[String(d?.id || '')]).length}</span>
          <span className="text-[10px] text-gray-500">pendente{consolidatedDocs.filter((d) => !viewedByDocId[String(d?.id || '')]).length === 1 ? '' : 's'}</span>
        </div>
      </div>

      {pendingConsentCount > 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
          <span className="text-xs text-amber-800">
            <strong>{pendingConsentCount} documento{pendingConsentCount === 1 ? '' : 's'}</strong> aguardando sua assinatura ou consentimento.
          </span>
        </div>
      ) : null}

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 shadow-sm text-xs text-red-800 whitespace-pre-wrap">{error}</div>
      ) : null}

      {loading ? (
        <div className="border border-gray-200 rounded-lg px-3 py-2 bg-white">
          <div className="text-xs text-gray-500 portal-loading-deferred">Carregando seus arquivos...</div>
        </div>
      ) : consolidatedDocs.length === 0 ? (
        <div className="border border-dashed border-gray-200 rounded-lg p-6 bg-gray-50 text-center">
          <Lock className="w-8 h-8 text-gray-200 mx-auto mb-2" />
          <div className="text-xs font-semibold text-[#20243B]">Cofre vazio</div>
          <div className="text-[11px] text-gray-500 mt-0.5">Procurações, contratos e documentos oficiais aparecerão aqui quando o escritório os publicar.</div>
        </div>
      ) : consolidatedDocs.length > 0 ? (
        <div className="space-y-4">
          {/* [UU-08 Fase F GAP-2] Seção destacada: Petições aguardando consentimento do cliente */}
          {(() => {
            const pendingPetitions = consolidatedDocs.filter(
              (r) =>
                String(r?.type || '').toUpperCase() === 'PETITION' &&
                String(r?.consentStatus || '').toLowerCase() === 'pending_consent'
            );
            if (pendingPetitions.length === 0) return null;
            return (
              <section className="mb-2 border border-[#C5A059]/40 rounded-lg bg-amber-50/30 px-3 py-2">
                <div className="flex items-center gap-2">
                  <Scale className="w-3.5 h-3.5 text-[#C5A059] shrink-0" />
                  <span className="text-xs font-semibold text-[#20243B] flex-1">Petições aguardando sua aprovação</span>
                  <span className="text-[9px] font-bold bg-[#C5A059] text-white px-1.5 py-0.5 rounded">{pendingPetitions.length}</span>
                </div>
                <div className="text-[10px] text-slate-600 mt-0.5">Exigem seu consentimento antes do protocolo judicial.</div>
              </section>
            );
          })()}
          {/* Cofre: contratos + petições + documentos de consentimento (workspace mode) */}
          {workspaceRows.length > 0 ? (
            (() => {
              const allFeatured = consolidatedDocs.filter((r) => {
                const t = String(r?.type || '').trim().toUpperCase();
                const c = String(r?.classification?.category || r?.category || '').toLowerCase();
                const isProcuration = t === 'PROCURATION' || c.includes('procura');
                if (isProcuration) return false;
                return t === 'CONTRACT' || c.includes('contrat') || t === 'PETITION';
              });
              const _ftKey = (r) => {
                const t = String(r?.type || '').trim().toUpperCase();
                const c = String(r?.classification?.category || r?.category || '').toLowerCase();
                if (t === 'PROCURATION' || c.includes('procura')) return 'PROCURATION';
                if (t === 'CONTRACT' || c.includes('contrat')) return 'CONTRACT';
                return t || c || 'OTHER';
              };
              const _ftTs = (r) => r?.createdAt?.toDate ? r.createdAt.toDate().getTime() : new Date(r?.createdAt || 0).getTime();
              const _ftMap = new Map();
              allFeatured.forEach((r) => {
                const k = _ftKey(r);
                if (!_ftMap.has(k) || _ftTs(r) > _ftTs(_ftMap.get(k))) _ftMap.set(k, r);
              });
              const featured = Array.from(_ftMap.values())
                .filter((r) => {
                  if (_ftKey(r) === 'PROCURATION') {
                    const cs = String(r?.consentStatus || '').trim().toLowerCase();
                    return cs !== 'accepted' && cs !== 'consent_accepted' && cs !== 'signed';
                  }
                  return true;
                })
                .sort((a, b) => {
                  const ord = { CONTRACT: 0, PROCURATION: 1 };
                  return (ord[_ftKey(a)] ?? 99) - (ord[_ftKey(b)] ?? 99);
                });
              if (featured.length === 0) return null;
              return (
                <>
                  {featured.length > 0 && (
                    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
                  {featured.map((row) => {
                    const rawTitle = resolveTitle(row);
                    const url = resolveFileUrl(row);
                    const canOpen = Boolean(url || String(row?.id || '').trim());
                    const category = String(row?.classification?.category || row?.category || '').trim() || 'Documento';
                    const date = toDateLabel(row?.createdAt || row?.signedAt || row?.updatedAt);
                    const viewed = Boolean(viewedByDocId?.[String(row?.id || '')]);
                    const pendingConsent = requiresPendingConsent(row);
                    const consentBadge = resolveConsentBadge(row);
                    const catLower = category.toLowerCase();
                    const isSignatureDoc = catLower.includes('procura') || catLower.includes('contrat');
                    const isReviewPending = consentBadge?.iconType === 'review_pending';
                    const isUploading = uploadingDocId === String(row?.id || '');
                    const isSigned = consentBadge?.iconType === 'accepted';
                    const needsSignatureActions = isSignatureDoc && !isUploading && !isReviewPending && !isSigned;
                    const textPending = isSignatureDoc ? 'AGUARDANDO ASSINATURA' : 'PENDENTE';
                    const textAccepted = isSignatureDoc ? 'ASSINADO' : 'ACEITO';
                    const tooltipPending = isSignatureDoc ? 'Baixe o PDF, assine via Gov.br ou à caneta, e envie de volta' : 'Clique para ler e dar consentimento';
                    return (
                      <div
                        key={row.id}
                        className="group/row flex items-center gap-3 px-3 py-2 bg-white border-b border-gray-100 last:border-b-0 hover:bg-[#C5A059]/5 transition-colors"
                        onDoubleClick={() => canOpen && handleViewDocument(row)}
                      >
                        <div className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                          {isSignatureDoc ? <Shield className="w-4 h-4 text-[#20243B]" /> : <FileText className="w-4 h-4 text-[#20243B]" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-bold text-[#20243B] truncate" title={rawTitle}>{rawTitle}</div>
                          {date !== '—' ? <div className="text-[11px] text-slate-500 truncate">Publicada em {date}</div> : null}
                        </div>
                        {!viewed ? <span className="shrink-0 text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">Novo</span> : null}
                        {Number(row?._mergedCount || 0) > 1 ? (
                          <span className="shrink-0 text-[10px] font-bold text-slate-600 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded" title={`${row._mergedCount} versões publicadas — você está vendo a mais recente`}>
                            {row._mergedCount} versões
                          </span>
                        ) : null}
                        <div className="flex items-center gap-1.5 shrink-0">
                          {needsSignatureActions ? (
                            <>
                              <button type="button" onClick={() => handleDownloadOriginal(row)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-300 bg-white text-[#20243B] text-[11px] font-bold hover:bg-slate-50 hover:border-[#C5A059] transition-colors">
                                <Download className="w-3.5 h-3.5 shrink-0" />Baixar
                              </button>
                              <a href="https://assinador.iti.br/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-blue-600 bg-blue-600 text-white text-[11px] font-bold hover:bg-blue-700 transition-colors cursor-pointer">
                                <Shield className="w-3.5 h-3.5 shrink-0" />Assinar Gov.br
                              </a>
                              <label className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-[#20243B] bg-[#20243B] text-white text-[11px] font-bold hover:bg-[#1a1e30] transition-colors cursor-pointer">
                                <Upload className="w-3.5 h-3.5 shrink-0" />Enviar Assinado
                                <input type="file" accept=".pdf,image/*" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUploadSignedDocument(f, row); e.target.value = ''; }} />
                              </label>
                            </>
                          ) : null}
                          {pendingConsent && !isSignatureDoc ? (
                            <button type="button" onClick={() => canOpen && handleViewDocument(row)} title="Leia o documento e dê seu consentimento" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-emerald-600 bg-emerald-600 text-white text-[11px] font-bold hover:bg-emerald-700 transition-colors">
                              <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />Dar Consentimento
                            </button>
                          ) : null}
                          <button type="button" onClick={() => canOpen && handleViewDocument(row)} disabled={!canOpen} title="Visualizar documento" className={`inline-flex items-center justify-center w-8 h-8 rounded-md border transition-colors ${canOpen ? 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-[#C5A059] hover:text-[#20243B]' : 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed'}`}>
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                        {isUploading || isReviewPending || pendingConsent || consentBadge?.iconType === 'accepted' ? (
                          <div className="shrink-0">
                            {isUploading ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-yellow-50 text-yellow-700 border border-yellow-300 text-[10px] font-bold animate-pulse"><RefreshCw className="w-3 h-3 animate-spin" />Enviando</span>
                            ) : isReviewPending ? (
                              <span title="Documento assinado enviado — aguardando análise do escritório" className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-yellow-50 text-yellow-700 border border-yellow-300 text-[10px] font-bold"><Clock className="w-3 h-3" />Em análise</span>
                            ) : pendingConsent ? (
                              <span title={tooltipPending} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-50 text-red-700 border border-red-200 text-[10px] font-bold cursor-pointer" onClick={() => canOpen && handleViewDocument(row)}><AlertTriangle className="w-3 h-3" />{textPending}</span>
                            ) : consentBadge?.iconType === 'accepted' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold"><CheckCircle2 className="w-3 h-3" />{textAccepted}</span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                    </div>
                  )}
                </>
              );
            })()
          ) : groupedByCaderno.map((group, groupIdx) => {
            const isAvulso = group.cadernoId === '__AVULSO__';
            return (
              <CadernoAccordion
                key={group.cadernoId}
                cadernoId={group.cadernoId}
                cadernoTitle={group.cadernoTitle || ''}
                docs={group.docs}
                isAvulso={isAvulso}
                defaultOpen={groupIdx === 0}
                processNumber={group.processNumber || ''}
                juizo={group.juizo || ''}
                comarca={group.comarca || ''}
                renderDocRow={(row) => {
            // Título real — vindo do Google Drive (via driveNamesByFileId) ou Firestore.
            // Fallback amigável quando Drive não retornar nome (raro: arquivo em pasta diferente).
            const rawTitleResolved = resolveTitle(row);
            const fallbackDate = toDateLabel(row?.createdAt || row?.addedAt || row?.updatedAt);
            const rawTitle = (rawTitleResolved && rawTitleResolved !== 'Documento')
              ? rawTitleResolved
              : (fallbackDate && fallbackDate !== '—' ? `Anexo enviado em ${fallbackDate}` : 'Anexo do caderno');
            const url = resolveFileUrl(row);
            const canOpen = Boolean(url || String(row?.id || '').trim());
            const category = String(row?.classification?.category || row?.category || '').trim() || 'Documento';
            const cadernoSuffix = !isAvulso && group.cadernoTitle ? ` — ${group.cadernoTitle}` : '';
            const displayCategory = `${category}${cadernoSuffix}`;
            const date = toDateLabel(row?.createdAt || row?.signedAt || row?.updatedAt);
            const viewed = Boolean(viewedByDocId?.[String(row?.id || '')]);
            const pendingConsent = requiresPendingConsent(row);
            const consentBadge = resolveConsentBadge(row);
            const catLower = category.toLowerCase();
            const isSignatureDoc = catLower.includes('procura') || catLower.includes('contrat');
            const isReviewPending = consentBadge?.iconType === 'review_pending';
            const isUploading = uploadingDocId === String(row?.id || '');
            const feedback = uploadFeedback[String(row?.id || '')] || null;
            const isSigned = consentBadge?.iconType === 'accepted';
            const needsSignatureActions = isSignatureDoc && !isUploading && !isReviewPending && !isSigned;

            const textPending = isSignatureDoc ? 'AGUARDANDO ASSINATURA' : 'PENDENTE';
            const textAccepted = isSignatureDoc ? 'ASSINADO' : 'ACEITO';
            const textDeclined = isSignatureDoc ? 'RECUSADO' : 'RECUSADO';
            const tooltipPending = isSignatureDoc ? 'Baixe o PDF, assine via Gov.br ou à caneta, e envie de volta' : 'Clique para ler e dar consentimento';
            const tooltipAccepted = isSignatureDoc ? 'Assinatura registrada' : 'Consentimento registrado';
            const tooltipDeclined = isSignatureDoc ? 'Assinatura recusada pelo cliente' : 'Consentimento recusado pelo cliente';

            return (
              <div
                key={row.id}
                className="group/row flex items-center gap-3 px-3 py-2 bg-white border-b border-gray-100 last:border-b-0 hover:bg-[#C5A059]/5 transition-colors"
                onDoubleClick={() => canOpen && handleViewDocument(row)}
              >
                {/* Ícone do tipo (sem bolinha vermelha — redundância com badge 'Novo' à direita) */}
                <div className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                  {catLower.includes('procura') ? <Shield className="w-4 h-4 text-[#20243B]" /> : <FileText className="w-4 h-4 text-[#20243B]" />}
                </div>

                {/* Título + subtitle com data apenas para procurações/contratos (distinguir múltiplas versões) */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="text-[13px] font-bold text-[#20243B] truncate" title={rawTitle}>
                      {rawTitle}
                    </div>
                    {String(row?.authenticityHash || '').trim() ? (
                      <button
                        type="button"
                        className="shrink-0 inline-flex items-center text-emerald-600 hover:text-emerald-800 opacity-0 group-hover/row:opacity-100 transition-opacity"
                        title={`SHA-256: ${row.authenticityHash}\n(clique para copiar)`}
                        onClick={(e) => { e.stopPropagation(); try { navigator.clipboard.writeText(String(row.authenticityHash)); } catch {} }}
                      >
                        <Lock className="w-3 h-3" />
                      </button>
                    ) : null}
                  </div>
                  {isSignatureDoc && date !== '—' ? (
                    <div className="text-[11px] text-slate-500 truncate">Publicada em {date}</div>
                  ) : null}
                </div>

                {/* Badges compactos: Novo + N versões */}
                {!viewed ? (
                  <span className="shrink-0 text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">Novo</span>
                ) : null}
                {Number(row?._mergedCount || 0) > 1 ? (
                  <span
                    className="shrink-0 text-[10px] font-bold text-slate-600 bg-slate-50 border border-slate-200 px-1.5 py-0.5 rounded"
                    title={`${row._mergedCount} versões publicadas — você está vendo a mais recente`}
                  >
                    {row._mergedCount} versões
                  </span>
                ) : null}

                {/* Ações compactas (assinatura completa quando aplicável) */}
                <div className="flex items-center gap-1.5 shrink-0">
                  {needsSignatureActions ? (
                    <>
                      <div className="group relative inline-flex">
                        <button
                          type="button"
                          onClick={() => handleDownloadOriginal(row)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-300 bg-white text-[#20243B] text-[11px] font-bold hover:bg-slate-50 hover:border-[#C5A059] transition-colors"
                        >
                          <Download className="w-3.5 h-3.5 shrink-0" />
                          Baixar
                        </button>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 rounded-lg bg-[#20243B] text-white text-[10px] font-bold whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity shadow-lg z-10">
                          PASSO 1: Baixe a via original (imprima e assine OU assine digitalmente).
                        </div>
                      </div>
                      {/* [UU-08 Fase F GAP-4] Opção intermediária: assinador Gov.br (ICP-Brasil gratuito) */}
                      <div className="group relative inline-flex">
                        <a
                          href="https://assinador.iti.br/"
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-blue-600 bg-blue-600 text-white text-[11px] font-bold hover:bg-blue-700 transition-colors cursor-pointer"
                        >
                          <Shield className="w-3.5 h-3.5 shrink-0" />
                          Assinar Gov.br
                        </a>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 rounded-lg bg-[#20243B] text-white text-[10px] font-bold whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity shadow-lg z-10">
                          OPCIONAL: Assinador ICP-Brasil gratuito. Abra em nova aba.
                        </div>
                      </div>
                      <div className="group relative inline-flex">
                        <label className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-[#20243B] bg-[#20243B] text-white text-[11px] font-bold hover:bg-[#1a1e30] transition-colors cursor-pointer">
                          <Upload className="w-3.5 h-3.5 shrink-0" />
                          Enviar Assinado
                          <input
                            ref={(el) => { if (el) uploadInputRefs.current[String(row.id)] = el; }}
                            type="file"
                            accept=".pdf,image/*"
                            capture="environment"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleUploadSignedDocument(file, row);
                              e.target.value = '';
                            }}
                          />
                        </label>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 rounded-lg bg-[#20243B] text-white text-[10px] font-bold whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity shadow-lg z-10">
                          PASSO 2: Anexe o documento assinado aqui.
                        </div>
                      </div>
                    </>
                  ) : null}

                  {/* Botão Dar Consentimento inline (para docs que pedem consentimento e NÃO são procuração/contrato) */}
                  {pendingConsent && !isSignatureDoc ? (
                    <button
                      type="button"
                      onClick={() => canOpen && handleViewDocument(row)}
                      title="Leia o documento e dê seu consentimento"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-emerald-600 bg-emerald-600 text-white text-[11px] font-bold hover:bg-emerald-700 transition-colors"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                      Dar Consentimento
                    </button>
                  ) : null}

                  <button
                    type="button"
                    onClick={() => canOpen && handleViewDocument(row)}
                    disabled={!canOpen}
                    title="Visualizar documento"
                    aria-label="Visualizar documento"
                    className={`inline-flex items-center justify-center w-8 h-8 rounded-md border transition-colors ${canOpen ? 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-[#C5A059] hover:text-[#20243B]' : 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed'}`}
                  >
                    <Eye className="w-4 h-4" />
                  </button>

                  {isAdmin ? (
                    <button
                      type="button"
                      onClick={() => handleRevokeDocument(row)}
                      title="Revogar e excluir documento"
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 bg-white text-red-400 text-xs font-bold hover:text-red-600 hover:bg-red-50 hover:border-red-400 transition-colors"
                    >
                      <Trash2 className="w-4 h-4 shrink-0" />
                    </button>
                  ) : null}
                </div>

                {/* Status badge compacto (só quando há estado especial) */}
                <div className="shrink-0">
                  {isUploading ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-yellow-50 text-yellow-700 border border-yellow-300 text-[10px] font-bold animate-pulse">
                      <RefreshCw className="w-3 h-3 animate-spin" />
                      Enviando
                    </span>
                  ) : isReviewPending ? (
                    <span title="Documento assinado enviado — aguardando análise do escritório" className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-yellow-50 text-yellow-700 border border-yellow-300 text-[10px] font-bold">
                      <Clock className="w-3 h-3" />
                      Em análise
                    </span>
                  ) : pendingConsent ? (
                    <span title={tooltipPending} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-50 text-red-700 border border-red-200 text-[10px] font-bold cursor-pointer" onClick={() => canOpen && handleViewDocument(row)}>
                      <AlertTriangle className="w-3 h-3" />
                      {textPending}
                    </span>
                  ) : consentBadge?.iconType === 'accepted' ? (
                    <span title={tooltipAccepted} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold">
                      <CheckCircle2 className="w-3 h-3" />
                      {textAccepted}
                    </span>
                  ) : consentBadge?.iconType === 'declined' ? (
                    <span title={tooltipDeclined} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-50 text-red-700 border border-red-200 text-[10px] font-bold">
                      <AlertTriangle className="w-3 h-3" />
                      {textDeclined}
                    </span>
                  ) : null}
                </div>

                {/* ── Feedback de upload ── */}
                {feedback ? (
                  <div className={`w-full mt-2 rounded-lg px-3 py-1.5 text-[10px] font-bold flex items-center gap-2 ${
                    feedback.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' :
                    feedback.type === 'error' ? 'bg-red-50 border border-red-200 text-red-700' :
                    'bg-yellow-50 border border-yellow-200 text-yellow-700'
                  }`}>
                    {feedback.type === 'loading' ? <RefreshCw className="w-3 h-3 animate-spin shrink-0" /> :
                     feedback.type === 'success' ? <CheckCircle2 className="w-3 h-3 shrink-0" /> :
                     <AlertTriangle className="w-3 h-3 shrink-0" />}
                    {feedback.text}
                  </div>
                ) : null}
              </div>
            );
                }}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
